import { AnimatePresence, Reorder, motion } from "framer-motion";
import { InstructionStep, InstructionStepImage } from "utils/interfaces";
import EmptyViewPopUp from "../common/EmptyViewPopUp";
import ScrollDownButton from "../common/ScrollDownButton";
import InstructionCard from "./InstructionCard";
import { useContext, useRef } from "react";
import { CreateEditInstructionsContext } from "components/utils/Contexts";
import useOverflow from "hooks/useOverflow";

const StepsList: React.FC = ({}) => {
  // Reference definition
  const instructionListRef = useRef<HTMLDivElement | null>(null);

  // Hook to check if the list is overflowing and scrolling
  const isInstructionListOverflowing = useOverflow(instructionListRef);

  // Get the context information
  const {
    instructionSteps,
    instructionIndexToEdit,
    setTitle,
    setParagraph,
    setInstructionIndexToEdit,
    setCreationTimestamp,
    setStepImageInfo,
    setCodeSoFarInfo,
    setInstructionSteps,
    setShowForm,
    setIncludeCodeSoFar,
  }: {
    instructionSteps: InstructionStep[];
    instructionIndexToEdit: number | null;
    setTitle: React.Dispatch<React.SetStateAction<string>>;
    setParagraph: React.Dispatch<React.SetStateAction<string>>;
    setInstructionIndexToEdit: React.Dispatch<
      React.SetStateAction<number | null>
    >;
    setCreationTimestamp: React.Dispatch<React.SetStateAction<string>>;
    setStepImageInfo: React.Dispatch<
      React.SetStateAction<InstructionStepImage>
    >;

    setCodeSoFarInfo: React.Dispatch<
      React.SetStateAction<InstructionStepImage>
    >;
    setInstructionSteps: React.Dispatch<
      React.SetStateAction<InstructionStep[]>
    >;
    setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
    setIncludeCodeSoFar: React.Dispatch<React.SetStateAction<boolean>>;
  } = useContext(CreateEditInstructionsContext);

  /**
   * Function to delete a question when pressed delete button
   */
  const deleteInstructionAction = (index: number) => {
    // And delete that specific index
    setInstructionSteps([
      ...instructionSteps.slice(0, index),
      ...instructionSteps.slice(index + 1),
    ]);
  };

  /**
   * Function to edit a question when pressed on the card
   */
  const editInstructionAction = (index: number) => {
    // Set the question to Edit
    setInstructionIndexToEdit(index);

    // Get the instruction content
    const instruction = instructionSteps[index];

    // And set the current values to edit
    setTitle(instruction.title);
    setParagraph(instruction.paragraph);
    setStepImageInfo(instruction.stepImage);
    setCodeSoFarInfo(instruction.codeSoFarImage);
    setCreationTimestamp(
      instruction.creationTimestamp ? instruction.creationTimestamp : "",
    );

    // Open the include code so far if there's any image
    setIncludeCodeSoFar(
      !!instruction.codeSoFarImage && !!instruction.codeSoFarImage.filename,
    );
    // Make sure the tab will be opened
    setShowForm(true);
  };

  /**
   * Function executed when clicking the scroll bottom list button
   * @returns
   */
  const handleScrollBottomList = () => {
    // Check if the container is defined
    const container = instructionListRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    });
  };

  return (
    <div className="relative w-[495px] h-full min-h-0 select-none">
      {instructionIndexToEdit !== null && (
        <div className="absolute w-full h-full min-h-0 z-10 cursor-not-allowed" />
      )}
      <div
        ref={instructionListRef}
        className={`w-full h-full flex-col justify-start items-start gap-8 inline-flex overflow-y-auto pop-up-scrollbar select-none${
          instructionIndexToEdit !== null ? " pl-3" : ""
        }`}
      >
        <AnimatePresence mode="wait">
          {instructionSteps && instructionSteps.length > 0 ? (
            <Reorder.Group
              key="instruction-list"
              axis="y"
              values={instructionSteps}
              onReorder={setInstructionSteps}
              className="w-full inline-flex flex-col gap-2"
            >
              <AnimatePresence>
                {instructionSteps.map(
                  (instruction: InstructionStep, index: number) => (
                    <InstructionCard
                      key={
                        instruction.creationTimestamp
                          ? instruction.creationTimestamp
                          : instruction.title + instruction.paragraph
                      }
                      index={index}
                      instruction={instruction}
                      deleteCardAction={deleteInstructionAction}
                      editCardAction={editInstructionAction}
                      isSelected={index === instructionIndexToEdit}
                    />
                  ),
                )}
              </AnimatePresence>
            </Reorder.Group>
          ) : (
            <motion.div
              key="empty-icon"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.15 } }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="w-full h-full inline-flex items-center justify-center"
            >
              <EmptyViewPopUp text="When a new step is added, it'll show up here" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* Floating scroll down button */}
      <ScrollDownButton
        show={isInstructionListOverflowing}
        onClick={handleScrollBottomList}
      />
    </div>
  );
};

export default StepsList;
